import _ from 'lodash'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'

import logoImg from '@/assets/img/logo.png'

import { closedMixin, openedMixin } from '@/utils/mui'
import { SidebarItem } from '@/utils/subscription/types'
import { Skeleton } from '@mui/material'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

export type SidebarProps = {
  isLoading?: boolean
  menuItems: SidebarItem[]
}

export const sidebarExpandedWidth = 200
export const sidebarCollapsedWidth = 60

type SidebarToggleButtonProps = {
  expanded: boolean
  onClick: () => void
  sidebarExpandedWidth: number
  sidebarCollapsedWidth: number
}

function SidebarToggleButton({ expanded, onClick, sidebarExpandedWidth, sidebarCollapsedWidth }: SidebarToggleButtonProps) {
  const theme = useTheme()
  return (
    <IconButton
      onClick={onClick}
      size="small"
      sx={[
        {
          backgroundColor: '#269CA6',
          position: 'fixed',
          top: '65px',
          left: expanded ? sidebarExpandedWidth : sidebarCollapsedWidth,
          transform: 'translate(-50%, -50%)',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '&:hover': {
            backgroundColor: '#269CA6',
          },
        },
        expanded && {
          transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        !expanded && {
          transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        },
      ]}
    >
      {expanded ? <ChevronLeftRoundedIcon htmlColor="#FFF" fontSize="small" /> : <ChevronRightRoundedIcon htmlColor="#FFF" fontSize="small" />}
    </IconButton>
  )
}

type SidebarLogoProps = {
  expanded: boolean
  logoUrl?: string
}

function SidebarLogo({ expanded, logoUrl }: SidebarLogoProps) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '80px', p: '16px' }}>
      {expanded ? (
        <img alt="logo" src={logoUrl ?? logoImg} height="48px" />
      ) : (
        <Avatar alt="logo" src={logoUrl ?? '/favicon.ico'} sx={{ width: 30, height: 30 }} />
      )}
    </Stack>
  )
}

type SidebarMenuItemProps = {
  menuItem: SidebarItem
  expanded: boolean
  pathname: string
}

function SidebarMenuItem({ menuItem, expanded, pathname }: SidebarMenuItemProps) {
  const { t } = useTranslation()
  const isMenuItemActive = menuItem.isPathBase ? menuItem.link === pathname : pathname.startsWith(menuItem.link)
  const Icon = menuItem.icon

  const button = (
    <Button
      className={isMenuItemActive ? 'active' : ''}
      color="primary"
      component={Link}
      to={menuItem.link}
      fullWidth
      startIcon={Icon ? <Icon htmlColor={isMenuItemActive ? '#28ACB0' : undefined} sx={{ fontSize: '24px !important' }} /> : undefined}
      sx={[
        {
          color: '#636363',
          fontFamily: "'Dongle', sans-serif",
          fontSize: '24px',
          fontWeight: 400,
          letterSpacing: '0.025em',
          lineHeight: '18px',
          textTransform: 'capitalize',
          justifyContent: 'start',
          minWidth: 'unset',
          pl: '4px',
          pr: '4px',
          '& .MuiButton-startIcon': {
            width: '32px',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
          },
        },
      ]}
    >
      <Box component="span" sx={{ position: 'absolute', left: '50px', transform: 'translateY(2px)' }}>
        {t(menuItem.label)}
      </Box>
    </Button>
  )

  return expanded ? (
    <Box key={menuItem.name}>{button}</Box>
  ) : (
    <Tooltip key={menuItem.name} arrow title={t(menuItem.label)} placement="right">
      {button}
    </Tooltip>
  )
}

type SidebarListProps = {
  isLoading: boolean
  menuItems: SidebarItem[]
  expanded: boolean
  pathname: string
  menuItemSpacing: number
  menuItemsStackWrapperRef: React.RefObject<HTMLDivElement>
}

function SidebarList({ isLoading, menuItems, expanded, pathname, menuItemSpacing, menuItemsStackWrapperRef }: SidebarListProps) {
  return (
    <Stack ref={menuItemsStackWrapperRef} justifyContent="center" spacing={menuItemSpacing}>
      {isLoading
        ? _.range(7).map((index) => <Skeleton key={index} variant="rounded" sx={{ width: '36px', height: '36px' }} />)
        : menuItems.map((menuItem) => <SidebarMenuItem key={menuItem.name} menuItem={menuItem} expanded={expanded} pathname={pathname} />)}
    </Stack>
  )
}

export function Sidebar(props: SidebarProps) {
  const { isLoading = false, menuItems } = props
  const { pathname } = useLocation()
  const theme = useTheme()
  const { height: menuItemsStackWrapperHeight, ref: menuItemsStackWrapperRef } = useResizeDetector()
  const menuItemsBoxWrapperRef = useRef<HTMLDivElement>(null)

  const [expanded, setExpanded] = useState<boolean>(false)
  const [menuItemSpacing, setMenuItemSpacing] = useState<number>(5)

  const authUser = useAppSelector(selectAuthUser)

  useLayoutEffect(() => {
    if (menuItemsBoxWrapperRef.current && menuItemsStackWrapperHeight) {
      const menuItemsBoxWrapperHeight = menuItemsBoxWrapperRef.current.getBoundingClientRect().height
      if (menuItemsStackWrapperHeight > menuItemsBoxWrapperHeight) setMenuItemSpacing((prevMenuItemSpacing) => Math.max(prevMenuItemSpacing - 1, 0))
    }
  }, [menuItemsStackWrapperHeight])

  const toggleSidebar = () => {
    setExpanded((prevExpanded) => !prevExpanded)
  }

  return (
    <Box sx={{ position: 'relative', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
      <SidebarToggleButton
        expanded={expanded}
        onClick={toggleSidebar}
        sidebarExpandedWidth={sidebarExpandedWidth}
        sidebarCollapsedWidth={sidebarCollapsedWidth}
      />
      <Drawer
        variant="permanent"
        sx={[
          {
            overflowX: 'hidden',
            height: '100vh',
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              borderRight: 'none',
              overflowX: 'hidden',
            },
          },
          expanded && {
            ...openedMixin(sidebarExpandedWidth)(theme),
            '& .MuiDrawer-paper': openedMixin(sidebarExpandedWidth)(theme),
          },
          !expanded && {
            ...closedMixin(sidebarCollapsedWidth)(theme),
            '& .MuiDrawer-paper': closedMixin(sidebarCollapsedWidth)(theme),
          },
        ]}
        PaperProps={{
          elevation: 2,
        }}
      >
        <Stack sx={{ height: '100%' }} spacing={2}>
          <SidebarLogo expanded={expanded} logoUrl={authUser?.establishment?.logo?.url} />
          <Box
            ref={menuItemsBoxWrapperRef}
            sx={{
              flex: 1,
              p: '0 10px',
              overflowX: 'hidden',
              overflowY: 'auto',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <SidebarList
              isLoading={isLoading}
              menuItems={menuItems}
              expanded={expanded}
              pathname={pathname}
              menuItemSpacing={menuItemSpacing}
              menuItemsStackWrapperRef={menuItemsStackWrapperRef as React.RefObject<HTMLDivElement>}
            />
          </Box>
        </Stack>
      </Drawer>
    </Box>
  )
}
